export type LifecycleRuleType =
  | "governance-lifecycle-delete"
  | "catalog-dependency-delete"
  | "song-safety-delete"
  | "append-only-retention"
  | "device-first-trust";

export interface LifecycleRulesTab {
  key: string;
  label: string;
  /** Optional short paragraph above the bullet list. */
  intro?: string;
  /** When true, render the Song lifecycle flow diagram above tab body. */
  showLifecycleDiagram?: boolean;
  /** Optional titled sections (e.g. “Why is this protected?”). */
  sections?: Array<{ title: string; body: string }>;
  bullets: string[];
  limitations?: string[];
}

export interface LifecycleRulesContent {
  ruleType: LifecycleRuleType;
  ruleTitle: string;
  flowSummary: string;
  /** Flat drawer body (Albums / Artists / Labels). Ignored when `tabs` is set. */
  bullets: string[];
  limitations?: string[];
  /** Tabbed drawer body (Song Library). When present, the drawer uses tabs. */
  tabs?: LifecycleRulesTab[];
}

export const RECORD_LABELS_LIFECYCLE_RULES: LifecycleRulesContent = {
  ruleType: "catalog-dependency-delete",
  ruleTitle: "Catalog Dependency Delete",
  flowSummary: "Active → Archive → Restore (optional) → Permanent Delete",
  bullets: [
    "Only Super Admins may archive, restore, or permanently delete record labels. Edit permission does not grant delete authority.",
    "Song Library is the sole owner of the Song lifecycle. Record Label Archive / Restore / Purge never delete, reassign, or otherwise mutate Songs.",
    "Active: View / Edit / Archive. Permanent Delete is never offered on Active.",
    "Archived: View / Restore / Permanent Delete. Soft archive keeps the label identity; Permanent Delete removes the catalog row and retains an audit tombstone.",
    "If living Songs (Active or Archived) still reference this label, Archive and Permanent Delete are both blocked until those Songs are resolved in Song Library.",
    "Permanent Delete requires typing DELETE plus a free-text reason — same confirmation as Song Library.",
    "Record Label Name is the only field managed in this UI. PRO affiliations remain deferred backend metadata.",
  ],
  limitations: [
    "Archive and Permanent Delete are Super Admin-only on both UI and API.",
    "Restore preserves archive history (when / why the label was archived) forever.",
    "There is no cascade delete from Record Labels to Songs.",
  ],
};

export const ARTISTS_LIFECYCLE_RULES: LifecycleRulesContent = {
  ruleType: "catalog-dependency-delete",
  ruleTitle: "Catalog Dependency Delete",
  flowSummary: "Active → Archive → Restore (optional) → Permanent Delete",
  bullets: [
    "Only Super Admins may archive, restore, or permanently delete artists. Edit permission does not grant delete authority.",
    "Song Library is the sole owner of the Song lifecycle. Artist Archive / Restore / Purge never delete, reassign, or otherwise mutate Songs.",
    "Active: View / Edit / Archive. Permanent Delete is never offered on Active.",
    "Archived: View / Restore / Permanent Delete. Soft archive keeps the artist identity; Permanent Delete removes the catalog row and retains an audit tombstone.",
    "If living Songs (Active or Archived) still reference this artist, Archive and Permanent Delete are both blocked until those Songs are resolved in Song Library.",
    "Permanent Delete requires typing DELETE plus a free-text reason — same confirmation as Song Library.",
  ],
  limitations: [
    "Archive and Permanent Delete are Super Admin-only on both UI and API.",
    "Restore preserves archive history forever.",
    "There is no cascade delete from Artists to Songs.",
  ],
};

export const ALBUMS_LIFECYCLE_RULES: LifecycleRulesContent = {
  ruleType: "catalog-dependency-delete",
  ruleTitle: "Catalog Dependency Delete",
  flowSummary: "Active → Archive → Restore (optional) → Permanent Delete",
  bullets: [
    "Only Super Admins may archive, restore, or permanently delete albums. Edit permission does not grant delete authority.",
    "Song Library is the sole owner of the Song lifecycle. Album Archive / Restore / Purge never delete, reassign, or otherwise mutate Songs.",
    "Active: Edit / Archive. Permanent Delete is never offered on Active.",
    "Archived: Restore / Permanent Delete. Soft archive keeps the album identity; Permanent Delete removes the catalog row and retains an audit tombstone.",
    "If living Songs (Active or Archived) still reference this album, Archive and Permanent Delete are both blocked until those Songs are resolved in Song Library.",
    "Permanent Delete requires typing DELETE plus a free-text reason — same confirmation as Song Library.",
  ],
  limitations: [
    "Archive and Permanent Delete are Super Admin-only on both UI and API.",
    "Restore preserves archive history forever.",
    "There is no cascade delete from Albums to Songs.",
  ],
};

export const EMPLOYEE_LIFECYCLE_RULES: LifecycleRulesContent = {
  ruleType: "governance-lifecycle-delete",
  ruleTitle: "Governance Lifecycle Delete",
  flowSummary: "Active → Suspended → Deleted/Tombstoned → Purged",
  bullets: [
    "Stricter than catalog defaults. Employee governance rows are retained for audit and security history.",
    "Suspend and restore require authorized governance roles and reasons where specified.",
    "Delete marks a tombstone; purge removes Auth0 identity and is Super Admin-only with typed confirmation.",
    "Append-only audit history records every lifecycle transition.",
  ],
};

export const SONG_LIFECYCLE_RULES: LifecycleRulesContent = {
  ruleType: "song-safety-delete",
  ruleTitle: "Song Library Lifecycle",
  flowSummary:
    "Active → Archived → Restored or Permanently Deleted → Deleted/Tombstone → Tombstone Purge",
  bullets: [],
  tabs: [
    {
      key: "lifecycle",
      label: "Lifecycle",
      showLifecycleDiagram: true,
      intro:
        "Song Library owns the canonical Song lifecycle. Artists, Albums, and Record Labels never archive or delete Songs.",
      bullets: [
        "Song Library manages living Songs (Active / Archived): playability, archive, restore, and permanent delete.",
        "Song Processing is separate — it manages upload/processing jobs only. Failed uploads that never became Songs stay there.",
        "Song History is the historical lifecycle record — one row per Song across Active, Archived, and Deleted.",
        "Active: Play / View / Archive. Permanent Delete is never offered on Active.",
        "Archived: View / Restore / Permanent Delete.",
      ],
    },
    {
      key: "archive-restore",
      label: "Archive & Restore",
      bullets: [
        "Archive is a soft lifecycle transition. The Song moves to Archived and is not playable.",
        "Archive removes the Song from all Channel Programming placements (staging, pending, and production).",
        "Prior Channel placements are frozen into Song History before removal.",
        "Restore returns the Song to Active and playable.",
        "Restore never automatically adds the Song back to former Channels. Operators must re-program Channels if needed.",
      ],
    },
    {
      key: "permanent-delete",
      label: "Permanent Delete",
      bullets: [
        "Available only from Archived — never from Active.",
        "Requires typing DELETE plus a free-text reason.",
        "Before confirmation, the system shows every affected Channel and rotation.",
        "Removes all Channel Programming references for that Song.",
        "Fails closed if Channel cleanup cannot complete or still finds live memberships — the Song is not permanently deleted.",
        "Leaves a Deleted/Tombstone record in Song History for review before Tombstone Purge.",
      ],
    },
    {
      key: "channel-programming",
      label: "Channel Programming",
      intro:
        "Channel impact is calculated by the server before Archive or Permanent Delete.",
      sections: [
        {
          title: "Why is this protected?",
          body:
            "Music playback must never stop. Before a Song can be archived or permanently deleted, KrossKast verifies that removing it will not leave required Channel Programming without playable music. If necessary, the operation is blocked until replacement Songs have been programmed.",
        },
      ],
      bullets: [
        "A server-authoritative preflight lists affected Channel and rotation names.",
        "Archive or Permanent Delete is blocked if removal would leave a required rotation or Channel with zero playable Songs.",
        "When blocked, add replacement Songs to those Channels/rotations, then try again.",
        "Bulk operations apply continuity checks to the full selected set, not one Song at a time.",
      ],
    },
    {
      key: "bulk-actions",
      label: "Bulk Actions",
      bullets: [
        "Select individual rows, or use Select All Matching for the current filters.",
        "Bulk Archive and Permanent Delete aggregate Channel impact and deduplicate affected Channels.",
        "Continuity and cleanup rules apply to the entire selected set.",
        "Bulk work is server-owned and durable — progress does not depend on keeping this browser tab open.",
      ],
    },
    {
      key: "song-history",
      label: "Song History",
      bullets: [
        "Song History keeps one row per canonical Song.",
        "Active, Archived, and Deleted tabs follow the Song Library lifecycle.",
        "History Detail shows lifecycle events, processing attempts, playback history, and prior Channel memberships.",
        "Failed uploads that never became Songs remain in Song Processing — they do not appear as Song History rows.",
        "Song Processing Detail and Song History Detail are separate surfaces.",
      ],
    },
    {
      key: "tombstone-purge",
      label: "Tombstone Purge",
      bullets: [
        "Super Admin-only. Available from Song History → Deleted.",
        "Supports row selection, Select All Matching, and bulk purge.",
        "Requires a reason and typing PURGE (not DELETE).",
        "Final hard delete: removes Song History records, Track tombstone, projections, and retained Song history.",
        "No restore afterward. Use Permanent Delete + review on Deleted before purging.",
      ],
    },
    {
      key: "permissions-safety",
      label: "Permissions & Safety",
      bullets: [
        "Archive, Restore, Permanent Delete, and Tombstone Purge require Super Admin delete authority — edit permission alone is not enough.",
        "Archive and Permanent Delete fail closed if Channel cleanup is incomplete or continuity would be broken.",
        "Restore never recreates Channel Programming.",
        "Song Processing permanent delete cannot remove jobs that still belong to a living (Active or Archived) Song.",
        "Tombstone Purge is irreversible and Super Admin-only.",
      ],
      limitations: [
        "Channel impact and continuity checks depend on current Channel Programming data at confirmation time.",
        "Restore does not offer a one-click “re-add to former Channels” action — re-programming is manual.",
      ],
    },
  ],
};

export const DEVICE_ASSIGNMENTS_LIFECYCLE_RULES: LifecycleRulesContent = {
  ruleType: "device-first-trust",
  ruleTitle: "Connected to Device Fleet V2",
  flowSummary: "Register serial → First Trust (10 min) → Player binds remotely → Enrolled",
  bullets: [],
  tabs: [
    {
      key: "overview",
      label: "Overview",
      intro:
        "Showing physical inventory and assignment data from kk-device. Serial Number is the permanent physical Player identity.",
      bullets: [
        "First Trust opens a short server-side first-bind window — it does not enroll cryptography or assign a location.",
        "Only Super Admins may open or revoke First Trust. Edit permission does not grant First Trust authority.",
        "Select Players, then Batch Actions → First Trust. Bulk authorization opens an independent 10-minute window per serial; partial failures do not roll back successes.",
        "Already-bound serials cannot be rebound to a different key (hard reject). Factory-reset recovery is remote First Trust re-enroll of the same serial.",
      ],
      limitations: [
        "First Trust is Super Admin-only on both UI and API.",
        "There is no USB, ADB, typed code, or on-device bootstrap secret.",
        "Register Player records the physical serial only. It does not enroll cryptography or assign a location.",
      ],
    },
    {
      key: "status",
      label: "Status",
      intro:
        "The Status column is inventory and assignment — where the Player sits. It is not cryptographic enrollment.",
      bullets: [
        "Box: In-warehouse — serial is registered in inventory and is not assigned to a location.",
        "Checkmark: Installed — assigned to a Master Account / location (for example 3M Group, LLC).",
        "Question mark: Unclaimed.",
        "X: Offline.",
        "Hover any Status icon for the same labels.",
      ],
    },
    {
      key: "first-trust",
      label: "First Trust",
      intro:
        "The First Trust column is cryptographic enrollment of the physical serial. Hover the shield for the current state.",
      bullets: [
        "Shield with plus: Ready — in-warehouse, pre-activation, unbound. Eligible for a 10-minute First Trust window.",
        "Hourglass: Awaiting Player — a window is open; the Player must connect and bind remotely.",
        "Empty shield: Not enrolled — the serial is known, but no Keystore identity is bound, and the Player is not warehouse-Ready (usually already installed / assigned). This is not the same as Status.",
        "Shield with check: Enrolled — a credential is bound to this serial.",
        "Clock: Expired window. Slash: Revoked window.",
        "If an enrolled Player factory-resets, open First Trust (re-enroll) on the same serial so it can bind a new key remotely.",
      ],
    },
  ],
};

export const APPEND_ONLY_LIFECYCLE_RULES: LifecycleRulesContent = {
  ruleType: "append-only-retention",
  ruleTitle: "Append-Only Retention",
  flowSummary: "No UI delete path",
  bullets: [
    "Audit logs, security events, and legal/compliance history are append-only.",
    "Retention is governed by domain doctrine and legal hold — not by operator delete actions.",
  ],
};
